import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Panel, PanelGroup, PanelResizeHandle } from 'react-resizable-panels';
import { useLab } from '../../contexts/LabContext';
import SectionContent from './SectionContent';
import QCM from '../interactive/QCM';
import FeedbackDisplay from '../interactive/FeedbackDisplay';
import DashboardSummary from '../dashboard/DashboardSummary';
import IntegratedTerminalEditor from '../interactive/IntegratedTerminalEditor';
import NotesEditor from '../interactive/NotesEditor';
import LearningPath from '../learning/LearningPath';
import Timer from '../interactive/Timer';
import StatusDisplay from '../dashboard/StatusDisplay';

import { Tabs, TabList, Tab, TabPanel } from 'react-tabs';
import { useTranslation } from 'react-i18next';

// Composant pour afficher les détails de la solution
const SolutionDetailsDisplay = ({ solution, explanation, tips, commonError, qcmSolutionContent, t }) => (
    <div className="mt-4 p-4 bg-gray-700 rounded-lg shadow-inner border border-green-500">
        {qcmSolutionContent ? (
            <>
                <h4 className="text-xl font-bold text-green-400 mb-3">{t('qcm_correct_answer_title') || 'Correct Answer Details'}:</h4>
                <p className="text-gray-200 text-lg mb-2">
                    {t('qcm_question_text') || 'Question'}: <span className="font-bold">{qcmSolutionContent.question}</span>
                </p>
                <pre className="bg-gray-800 p-3 rounded-md text-green-300 overflow-x-auto whitespace-pre-wrap">
                    {qcmSolutionContent.answer}
                </pre>
                {qcmSolutionContent.explanation && (
                    <div className="mt-4">
                        <h5 className="font-bold text-blue-300 mb-2">{t('qcm_answer_explanation') || 'Explanation'}:</h5>
                        <SectionContent content={qcmSolutionContent.explanation} className="text-gray-300" />
                    </div>
                )}
                <hr className="my-6 border-gray-600" />
            </>
        ) : (
            solution && (
                <>
                    <h4 className="text-xl font-bold text-green-400 mb-3">{t('solution')}:</h4>
                    {/* Utilise SectionContent pour afficher la solution comme du code si nécessaire */}
                    <SectionContent content={solution} className="bg-gray-800 p-3 rounded-md text-green-300 overflow-x-auto whitespace-pre-wrap" isCode={true} />
                </>
            )
        )}

        {explanation && (
            <div className="mt-4">
                <h5 className="font-bold text-blue-300 mb-2">{t('explanation')}:</h5>
                <SectionContent content={explanation} className="text-gray-300" />
            </div>
        )}
        {tips && tips.length > 0 && (
            <div className="mt-4">
                <h5 className="font-bold text-purple-300 mb-2">{t('tips')}:</h5>
                <ul className="list-disc list-inside text-gray-300">
                    {tips.map((tip, index) => <li key={index}>{tip}</li>)}
                </ul>
            </div>
        )}
        {commonError && (
            <div className="mt-4">
                <h5 className="font-bold text-red-400 mb-2">{t('common_error')}:</h5>
                <p className="text-gray-300">{commonError}</p>
            </div>
        )}
    </div>
);


const LabViewer = () => {
    const { t } = useTranslation();
    const {
        labData: lab,
        currentSection,
        loading,
        error,
        feedback,
        handleQCMSubmission,
        setTerminalWriter,
        submitTerminalResponse,
        options,
        handleOption,
        displayMode,
        qcmSolutionContent,
        showNavigationButton,
        navigationButtonLabel,
        navigationButtonAction,
        wrongAnswerCount, // Récupère le compteur de mauvaises réponses depuis LabContext
        maxTime,
        isTimerRunning,
        setIsTimerRunning,
        attemptCount,
        handleTimeUp
    } = useLab();

    // Créer une ref pour handleOption pour éviter les problèmes de closure
    const handleOptionRef = useRef(handleOption);
    useEffect(() => {
        handleOptionRef.current = handleOption;
    }, [handleOption]);

    const handleTerminalContentChange = useCallback(() => {
        if (maxTime > 0 && !isTimerRunning) {
            setIsTimerRunning(true);
        }
    }, [maxTime, isTimerRunning, setIsTimerRunning]);

    const handleTerminalSubmit = useCallback((command, writeOutput, fileContent) => {
        // Timer is now stopped in LabContext on correct answer only
        // No need to setIsTimerRunning(false) here anymore
        const fileRef = fileContent ? { name: currentSection.file_path, content: fileContent } : null;
        submitTerminalResponse(command, fileRef);
    }, [submitTerminalResponse, currentSection]);



    const [renderPanels, setRenderPanels] = useState(false);

    useEffect(() => {
        if (!loading && lab && currentSection) {
            const timer = setTimeout(() => {
                setRenderPanels(true);
            }, 50);
            return () => clearTimeout(timer);
        } else {
            setRenderPanels(false);
        }
    }, [loading, lab, currentSection]);


    if (loading) {
        return <div className="flex items-center justify-center h-screen bg-gray-900 text-blue-400 text-xl">{t('loadingLab')}</div>;
    }

    if (error) {
        return <div className="flex items-center justify-center h-screen bg-gray-900 text-red-500 text-xl">{t('error')} : {error}</div>;
    }

    if (!lab) {
        return <div className="flex items-center justify-center h-screen bg-gray-900 text-red-500 text-xl">{t('noLabSelected')}</div>;
    }

    if (!currentSection) {
        return <div className="flex items-center justify-center h-screen bg-gray-900 text-red-500 text-xl">{t('noCurrentSubSection')}</div>;
    }

    if (!renderPanels) {
        return <div className="flex items-center justify-center h-screen bg-gray-900 text-blue-400 text-xl">{t('preparingLayout') || 'Préparation de la mise en page...'}</div>;
    }

    // Gère l'affichage du feedback et des icônes
    const getFeedbackMessage = (feedbackType, message) => {
        if (!message) return '';

        let iconLeft = '';
        let iconRight = '';
        let textColorClass = '';

        if (feedbackType === 'success') {
            iconLeft = '🏆 ';
            iconRight = ' 🏆';
            textColorClass = 'text-green-400';
        } else if (feedbackType === 'error') { // Changé de 'incorrect' à 'error' pour correspondre au type de feedback
            textColorClass = 'text-red-500';
            if (wrongAnswerCount === 1) { // Première mauvaise réponse
                iconLeft = '🙁 ';
                iconRight = ' 🙁';
            } else if (wrongAnswerCount === 2) { // Deuxième mauvaise réponse
                iconLeft = '😞 ';
                iconRight = ' ';
            } else if (wrongAnswerCount >= 3) { // À partir de la troisième
                iconLeft = '😩 ';
                iconRight = ' 😩';
            } else { // Cas par défaut ou initial (wrongAnswerCount est 0 avant la première mauvaise réponse)
                iconLeft = '⚠️ ';
                iconRight = ' ⚠️';
            }
        }

        return (
            <p className={`text-xl font-bold ${textColorClass}`}>
                {iconLeft}{message}{iconRight}
            </p>
        );
    };

    // Fonction pour rendre le contenu dynamique en fonction de displayMode
    const renderDynamicContent = () => {
        switch (displayMode) {
            case 'qcm_view':
                return currentSection.qcm && currentSection.qcm.question ? (
                    <QCM
                        qcmData={currentSection.qcm}
                        onSubmit={handleQCMSubmission}
                        showSolutionButton={true} // Ajouté pour afficher le bouton "Get Solution"
                        onGetSolution={() => handleOptionRef.current('solution')} // Appel handleOption pour la solution
                    />
                ) : (
                    <div className="text-red-400 p-4">{t('no_qcm_available')}</div>
                );
            case 'solution_view':
                return (
                    <SolutionDetailsDisplay
                        solution={currentSection.solution}
                        explanation={currentSection.explanation}
                        tips={currentSection.tips}
                        commonError={currentSection.common_error}
                        qcmSolutionContent={qcmSolutionContent}
                        t={t}
                    />
                );
            case 'instruction':
            default:
                return null; // Le contexte et la description sont rendus en dehors de ce switch
        }
    };

    // Détermine le titre de la tâche/quiz
    // Remplacé 'quizTitle' par 'QUIZ' directement
    const taskTitle = displayMode === 'qcm_view' ? 'QUIZ' : (t('currentTask') || 'Tâche Actuelle');

    return (
        <div className="flex flex-col h-screen bg-gray-900 text-gray-200 font-sans p-4 space-y-4">
            <PanelGroup direction="horizontal" className="flex-grow w-full rounded-xl shadow-2xl overflow-hidden border border-gray-700">

                <Panel defaultSize={60} minSize={30} className="flex flex-col p-6 bg-gray-800 border-r border-gray-700 overflow-y-auto custom-scrollbar rounded-l-xl relative">

                    <div className="absolute top-4 right-4">
                        <StatusDisplay>
                            {maxTime > 0 && (
                                <Timer
                                    key={currentSection.sub_instruction_id + displayMode}
                                    maxTimeInSeconds={maxTime}
                                    isRunning={isTimerRunning}
                                    onComplete={handleTimeUp}
                                />
                            )}
                            <div className="bg-gray-700 rounded-lg px-3 py-1 text-center">
                                <div className="text-gray-400 text-xs font-bold uppercase">{t('attempt_counter') || 'Tentatives'}</div>
                                <div className="text-white font-bold text-2xl">{attemptCount}</div>
                            </div>
                        </StatusDisplay>
                    </div>

                    <div className="mb-6 pb-4 text-center border-b border-gray-700">
                        <h2 className="text-blue-400 text-3xl font-bold mb-3">
                            {lab.instruction_title || lab.title}
                            {lab.metadata?.instruction_typology && (
                                <span className="ml-3 text-blue-400 text-2xl font-bold italic opacity-80">
                                    ({lab.metadata.instruction_typology})
                                </span>
                            )}
                        </h2>
                        {lab.instruction_description && <p className="text-gray-400 text-lg leading-relaxed">{lab.instruction_description}</p>}
                    </div>

                    <div className="flex-grow overflow-y-auto pr-4 mb-6 custom-scrollbar">
                        <h3 className="text-blue-300 text-xl font-semibold mb-4 text-left">{taskTitle}</h3>

                        {/* CONTEXTE DE LA SOUS-INSTRUCTION - Visible uniquement en mode instruction */}
                        {displayMode === 'instruction' && currentSection.sub_instruction_contexte && (
                            <div className="mb-5">
                                <p className="font-bold mb-2 text-blue-300">{t('context') || 'Contexte'}:</p>
                                <SectionContent content={currentSection.sub_instruction_contexte} />
                            </div>
                        )}
                        {/* DESCRIPTION DE LA SOUS-INSTRUCTION - Visible uniquement en mode instruction */}
                        {displayMode === 'instruction' && currentSection.sub_instruction_description && (
                            <div className="bg-gray-700 p-4 rounded-lg shadow-inner mb-5 border border-gray-600">
                                <p className="font-bold mb-2 text-blue-300">{t('description') || 'Description'}:</p>
                                <SectionContent content={currentSection.sub_instruction_description} />
                            </div>
                        )}

                        {/* AFFICHE LE FEEDBACK DU LLM SI PRÉSENT */}
                        {feedback && (
                            <div className="mt-6 pt-6 border-t border-dashed border-blue-400 flex flex-col items-center gap-4">
                                <FeedbackDisplay
                                    message={getFeedbackMessage(feedback.type, feedback.message)} // Utilise la fonction de formatage
                                    type={feedback.type}
                                />
                            </div>
                        )}

                        {/* Rendre le contenu dynamique (QCM ou Solution) après le feedback */}
                        {renderDynamicContent()} {/* Rendre ici le QCM ou la Solution si activé */}

                        {/* AFFICHE LES BOUTONS DE NAVIGATION QUAND LA SOLUTION EST AFFICHÉE */}
                        {displayMode === 'solution_view' && options.length > 0 && (
                            <div className="mt-4 flex justify-center gap-3 p-4 bg-gray-700 rounded-lg">
                                {options.map(option => (
                                    <button
                                        key={option.id}
                                        onClick={option.action}
                                        className="px-6 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg shadow-md text-white font-medium"
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* AFFICHE LES OPTIONS (QCM, Solution, Aide) SEULEMENT EN MODE 'instruction', MAIS PAS 'Explanation' SI FEEDBACK EST SUCCESS */}
                        {displayMode === 'instruction' && options.length > 0 && (
                            <div className="mt-6 pt-6 border-t border-dashed border-blue-400 flex flex-col items-center gap-4">
                                <div className="flex flex-wrap justify-center gap-3">
                                    {options
                                        .filter(option => !(feedback?.type === 'success' && option.label === t('option_explain')))
                                        .map((option, index) => (
                                            <button
                                                key={index}
                                                onClick={option.action}
                                                className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded-full shadow-lg transition-all duration-200 ease-in-out transform hover:scale-105 active:scale-95"
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="flex justify-end pt-4 border-t border-gray-700">
                        {showNavigationButton && navigationButtonAction && (
                            <button onClick={navigationButtonAction} className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2 px-5 rounded-full shadow-lg transition-all duration-200 ease-in-out transform hover:scale-105 active:scale-95">
                                {navigationButtonLabel}
                            </button>
                        )}
                    </div>
                </Panel>

                <PanelResizeHandle className="w-2 bg-gray-700 hover:bg-blue-500 cursor-ew-resize transition-colors duration-200" />

                <Panel defaultSize={40} minSize={20} className="flex flex-col h-full bg-gray-800 rounded-r-xl">
                    <PanelGroup direction="vertical">
                        <Panel defaultSize={30} minSize={10} className="flex flex-col h-full overflow-hidden bg-gray-800 rounded-tr-xl">
                            <Tabs className="react-tabs-custom flex flex-col h-full">
                                <TabList className="flex border-b border-gray-700 bg-gray-900 rounded-tr-xl">
                                    <Tab className="py-3 px-6 text-lg font-semibold text-gray-400 hover:text-blue-300 cursor-pointer border-b-2 border-transparent ui-selected:border-blue-500 ui-selected:text-blue-200 transition-colors duration-200">
                                        {t('terminal')}
                                    </Tab>
                                    <Tab className="py-3 px-6 text-lg font-semibold text-gray-400 hover:text-blue-300 cursor-pointer border-b-2 border-transparent ui-selected:border-blue-500 ui-selected:text-blue-200 transition-colors duration-200">
                                        {t('dashboard')}
                                    </Tab>
                                    <Tab className="py-3 px-6 text-lg font-semibold text-gray-400 hover:text-blue-300 cursor-pointer border-b-2 border-transparent ui-selected:border-blue-500 ui-selected:text-blue-200 transition-colors duration-200">
                                        {t('myNotes')}
                                    </Tab>
                                    <Tab className="py-3 px-6 text-lg font-semibold text-gray-400 hover:text-blue-300 cursor-pointer border-b-2 border-transparent ui-selected:border-blue-500 ui-selected:text-blue-200 transition-colors duration-200">
                                        {t('learningPath')}
                                    </Tab>
                                </TabList>

                                <TabPanel className="flex-grow bg-gray-800 overflow-hidden">
                                    <IntegratedTerminalEditor
                                        setWriterRef={setTerminalWriter}
                                        onSubmit={handleTerminalSubmit}
                                        onContentChange={handleTerminalContentChange}
                                        initialFileContent={currentSection?.base_file_content || ''}
                                        filePath={currentSection?.file_path || ''}
                                    />
                                </TabPanel>
                                <TabPanel className="flex-grow p-4 bg-gray-800 overflow-y-auto custom-scrollbar">
                                    <h4 className="text-xl font-bold text-purple-400 mb-4 text-center">{t('dashboard')}</h4>
                                    <DashboardSummary />
                                </TabPanel>
                                <TabPanel className="flex-grow p-4 bg-gray-800 overflow-y-auto custom-scrollbar">
                                    <h4 className="text-xl font-bold text-purple-400 mb-4 text-center">{t('notesEditor')}</h4>
                                    <NotesEditor />
                                </TabPanel>
                                <TabPanel className="flex-grow p-4 bg-gray-800 overflow-y-auto custom-scrollbar">
                                    <h4 className="text-xl font-bold text-purple-400 mb-4 text-center">{t('learningPath')}</h4>
                                    <LearningPath />
                                </TabPanel>
                            </Tabs>
                        </Panel>



                    </PanelGroup>
                </Panel>

            </PanelGroup>
        </div>
    );
};

export default LabViewer;
